import { buildUrl } from "../util/integration"

import {
    authorizedFetch,
} from "./http_api";

export {
    authorizedFetch,
};

export const SALE_MODE =
    Object.freeze({
        OFFER: "OFFER",
        AUCTION: "AUCTION",
    });

export const SALE_TYPE =
    Object.freeze({
        B2B: "B2B",
        B2C: "B2C",
    });

const ERROR_MESSAGE =
    Object.freeze({
        invalidSellerId:
            "Die Verkäufer-ID ist ungültig.",

        invalidLeadId:
            "Die Lead-ID ist ungültig.",

        invalidCategoryId:
            "Die Kategorie-ID ist ungültig.",

        invalidPrice:
            "Der Preis ist ungültig.",

        invalidMode:
            "Der Verkaufsmodus ist ungültig.",

        invalidType:
            "Der Verkaufstyp ist ungültig.",

        invalidTags:
            "Die Tags sind ungültig.",

        invalidRequest:
            "saleRequest muss eine Instanz von SaleCreationRequest sein.",
    });

const normalizeOptionalText = (
    value,
) => {
    if (
        value === null
        || value === undefined
    ) {
        return null;
    }

    const normalizedValue =
        String(value).trim();

    return (
        normalizedValue.length > 0
            ? normalizedValue
            : null
    );
};

const normalizePositiveId = (
    value,
    errorMessage,
) => {
    const normalizedValue =
        Number(value);

    if (
        !Number.isSafeInteger(
            normalizedValue,
        )
        || normalizedValue <= 0
    ) {
        throw new Error(
            errorMessage,
        );
    }

    return normalizedValue;
};

const normalizeSellerId = (
    value,
) => {
    return normalizePositiveId(
        value,
        ERROR_MESSAGE
            .invalidSellerId,
    );
};

const normalizeLeadId = (
    value,
) => {
    return normalizePositiveId(
        value,
        ERROR_MESSAGE.invalidLeadId,
    );
};

const normalizeCategoryId = (
    value,
) => {
    const normalizedValue =
        Number(value);

    if (
        !Number.isSafeInteger(
            normalizedValue,
        )
        || normalizedValue < 0
    ) {
        throw new Error(
            ERROR_MESSAGE
                .invalidCategoryId,
        );
    }

    return normalizedValue;
};

const normalizePrice = (
    value,
) => {
    const normalizedValue =
        Number(value);

    if (
        !Number.isSafeInteger(
            normalizedValue,
        )
        || normalizedValue < 1
    ) {
        throw new Error(
            ERROR_MESSAGE.invalidPrice,
        );
    }

    return normalizedValue;
};

const normalizeTags = (
    tags,
) => {
    if (
        tags === null
        || tags === undefined
    ) {
        return [];
    }

    if (!Array.isArray(tags)) {
        throw new Error(
            ERROR_MESSAGE.invalidTags,
        );
    }

    return [
        ...new Set(
            tags.map((tagId) => {
                const normalizedTagId =
                    Number(tagId);

                if (
                    !Number.isSafeInteger(
                        normalizedTagId,
                    )
                    || normalizedTagId < 0
                ) {
                    throw new Error(
                        ERROR_MESSAGE
                            .invalidTags,
                    );
                }

                return normalizedTagId;
            }),
        ),
    ];
};

const normalizeMode = (
    mode,
) => {
    if (
        !Object.values(
            SALE_MODE,
        ).includes(mode)
    ) {
        throw new Error(
            ERROR_MESSAGE.invalidMode,
        );
    }

    return mode;
};

const normalizeType = (
    type,
) => {
    if (
        !Object.values(
            SALE_TYPE,
        ).includes(type)
    ) {
        throw new Error(
            ERROR_MESSAGE.invalidType,
        );
    }

    return type;
};

const normalizePartner = (
    partner = {},
) => {
    return {
        businessName:
            normalizeOptionalText(
                partner.businessName,
            ),

        firstName:
            normalizeOptionalText(
                partner.firstName,
            ),

        lastName:
            normalizeOptionalText(
                partner.lastName,
            ),

        birthDate:
            normalizeOptionalText(
                partner.birthDate,
            ),

        phoneNumber:
            normalizeOptionalText(
                partner.phoneNumber,
            ),

        emailAddress:
            normalizeOptionalText(
                partner.emailAddress,
            ),
    };
};

const normalizeAddress = (
    address = {},
) => {
    return {
        street:
            normalizeOptionalText(
                address.street,
            ),

        houseNo:
            normalizeOptionalText(
                address.houseNo,
            ),

        zipCode:
            normalizeOptionalText(
                address.zipCode,
            ),

        town:
            normalizeOptionalText(
                address.town,
            ),
    };
};

const normalizeComments = (
    comments = {},
) => {
    return {
        publicComment:
            normalizeOptionalText(
                comments.publicComment,
            ),

        privateComment:
            normalizeOptionalText(
                comments.privateComment,
            ),
    };
};

const buildRequestUrl = (
    endpoint,
    queryParameters = {},
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        queryParameters,
    ).forEach(([
        name,
        value,
    ]) => {
        if (
            value === null
            || value === undefined
        ) {
            return;
        }

        if (Array.isArray(value)) {
            /*
             * Auch bei einem öffentlichen
             * Verkauf erwartet der Controller
             * den Parameter `tag`.
             *
             * Eine leere Liste wird deshalb
             * als `tag=` übertragen.
             */
            if (value.length === 0) {
                query.append(
                    name,
                    "",
                );

                return;
            }

            value.forEach(
                (arrayValue) => {
                    query.append(
                        name,
                        String(
                            arrayValue,
                        ),
                    );
                },
            );

            return;
        }

        query.set(
            name,
            String(value),
        );
    });

    const queryString =
        query.toString();

    return queryString === ""
        ? buildUrl(endpoint)
        : buildUrl(
            `${endpoint}?${queryString}`,
        );
};

export class SaleCreationRequest {
    constructor({
        sellerId,
        mode,
        price,
        tags = [],
        type,
        partner = {},
        address = {},
        categoryId,
        comments = {},
    }) {
        this.sellerId =
            normalizeSellerId(
                sellerId,
            );

        this.mode =
            normalizeMode(mode);

        this.price =
            normalizePrice(price);

        this.tags =
            normalizeTags(tags);

        this.type =
            normalizeType(type);

        this.partner =
            normalizePartner(
                partner,
            );

        this.address =
            normalizeAddress(
                address,
            );

        this.categoryId =
            normalizeCategoryId(
                categoryId,
            );

        this.comments =
            normalizeComments(
                comments,
            );
    }

    static offer(
        properties,
    ) {
        return new SaleCreationRequest({
            ...properties,
            mode: SALE_MODE.OFFER,
        });
    }

    static auction(
        properties,
    ) {
        return new SaleCreationRequest({
            ...properties,
            mode: SALE_MODE.AUCTION,
        });
    }

    static b2bOffer(
        properties,
    ) {
        return SaleCreationRequest
            .offer({
                ...properties,
                type: SALE_TYPE.B2B,
            });
    }

    static b2cOffer(
        properties,
    ) {
        return SaleCreationRequest
            .offer({
                ...properties,
                type: SALE_TYPE.B2C,
            });
    }

    static b2bAuction(
        properties,
    ) {
        return SaleCreationRequest
            .auction({
                ...properties,
                type: SALE_TYPE.B2B,
            });
    }

    static b2cAuction(
        properties,
    ) {
        return SaleCreationRequest
            .auction({
                ...properties,
                type: SALE_TYPE.B2C,
            });
    }

    static from(
        value,
    ) {
        if (
            value
            instanceof SaleCreationRequest
        ) {
            return value;
        }

        return new SaleCreationRequest(
            value,
        );
    }

    isOffer() {
        return (
            this.mode
            === SALE_MODE.OFFER
        );
    }

    isAuction() {
        return (
            this.mode
            === SALE_MODE.AUCTION
        );
    }

    isPrivate() {
        return (
            this.tags.length > 0
        );
    }

    toJSON() {
        return {
            sellerId:
                this.sellerId,

            mode:
                this.mode,

            price:
                this.price,

            tags:
                [...this.tags],

            type:
                this.type,

            businessName:
                this.partner
                    .businessName,

            firstName:
                this.partner
                    .firstName,

            lastName:
                this.partner
                    .lastName,

            birthDate:
                this.partner
                    .birthDate,

            phoneNumber:
                this.partner
                    .phoneNumber,

            emailAddress:
                this.partner
                    .emailAddress,

            street:
                this.address
                    .street,

            houseNo:
                this.address
                    .houseNo,

            zipCode:
                this.address
                    .zipCode,

            town:
                this.address
                    .town,

            categoryId:
                this.categoryId,

            publicComment:
                this.comments
                    .publicComment,

            privateComment:
                this.comments
                    .privateComment,
        };
    }
}

export const createSaleAPI = (
    accessToken,
    saleRequest,
) => {
    if (
        !(
            saleRequest
            instanceof SaleCreationRequest
        )
    ) {
        throw new TypeError(
            ERROR_MESSAGE
                .invalidRequest,
        );
    }

    return authorizedFetch(
        accessToken,
        buildUrl("sale"),
        {
            method: "POST",

            headers: {
                "Content-Type":
                    "application/json",
            },

            body: JSON.stringify(
                saleRequest,
            ),
        },
    );
};

export const fetchSaleTagsAPI = (
    accessToken,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl("sale/tags"),
        {
            method: "GET",
        },
    );
};

export const resellLeadAPI = (
    accessToken,
    leadId,
    price,
    tagIds = [],
) => {
    return authorizedFetch(
        accessToken,

        buildRequestUrl(
            `sale/lead/${
                normalizeLeadId(
                    leadId,
                )
            }/resale`,
            {
                price:
                    normalizePrice(
                        price,
                    ),

                tag:
                    normalizeTags(
                        tagIds,
                    ),
            },
        ),

        {
            method: "PUT",
        },
    );
};

export const reauctionLeadAPI = (
    accessToken,
    leadId,
    minBid,
    tagIds = [],
) => {
    return authorizedFetch(
        accessToken,

        buildRequestUrl(
            `sale/lead/${
                normalizeLeadId(
                    leadId,
                )
            }/reauction`,
            {
                minBid:
                    normalizePrice(
                        minBid,
                    ),

                tag:
                    normalizeTags(
                        tagIds,
                    ),
            },
        ),

        {
            method: "PUT",
        },
    );
};